import { useEffect, useState } from 'react';
import { Truck } from 'lucide-react';
import { useServiceabilityCheck } from '../hooks/use-delivery.js';
import { formatPaise } from '../../../../lib/format.js';
import type { ServiceabilityReason } from '../types/delivery.js';

const PINCODE_PATTERN = /^[1-9][0-9]{5}$/;
const STORAGE_KEY = 'tsg:last-pincode';

const REASON_COPY: Record<ServiceabilityReason, string> = {
  not_listed: "We don't deliver to this pincode yet.",
  pincode_disabled: 'Delivery to this pincode is temporarily paused.',
  zone_disabled: 'Delivery to this area is temporarily unavailable.',
  no_active_fee_rule: "Delivery isn't set up for this area yet.",
};

/**
 * Shared pincode-serviceability checker, used on cart/checkout (and
 * mirrored, not reused verbatim, by the product detail page's own inline
 * implementation -- see `product-detail-page.tsx`'s "Check delivery"
 * section for the original, untouched pattern this one follows). The
 * entered pincode is remembered in `sessionStorage` purely for convenience
 * (pre-filling the input on a later visit within the same tab) -- it is
 * NEVER treated as proof of serviceability by itself; every render still
 * calls the real backend endpoint via `useServiceabilityCheck` before
 * showing a result, exactly the "always validate using backend" rule the
 * checkout/order-creation flow already enforces server-side regardless of
 * what this component displays.
 */
export function DeliveryChecker() {
  const [pincode, setPincode] = useState(() => {
    try {
      return sessionStorage.getItem(STORAGE_KEY) ?? '';
    } catch {
      return '';
    }
  });
  const [touched, setTouched] = useState(false);
  const serviceability = useServiceabilityCheck();

  useEffect(() => {
    if (!PINCODE_PATTERN.test(pincode)) return;
    try {
      sessionStorage.setItem(STORAGE_KEY, pincode);
    } catch {
      // sessionStorage unavailable (private browsing, etc.) -- convenience-only, safe to skip.
    }
  }, [pincode]);

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setTouched(true);
    if (!PINCODE_PATTERN.test(pincode)) return;
    serviceability.mutate(pincode);
  }

  return (
    <div className="rounded-2xl border border-gray-100 bg-white p-4">
      <div className="mb-2 flex items-center gap-2">
        <Truck className="h-4 w-4 text-gray-400" aria-hidden="true" />
        <h2 className="text-sm font-semibold text-gray-900">Check delivery</h2>
      </div>
      <form onSubmit={handleSubmit} className="flex gap-2">
        <label htmlFor="cart-pincode-input" className="sr-only">
          Pincode
        </label>
        <input
          id="cart-pincode-input"
          inputMode="numeric"
          placeholder="Enter pincode"
          value={pincode}
          onChange={(event) => setPincode(event.target.value.replace(/\D/g, '').slice(0, 6))}
          className="focus-visible:ring-brand w-full rounded-full border border-gray-200 px-3 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2"
        />
        <button
          type="submit"
          disabled={serviceability.isPending}
          className="focus-visible:ring-brand shrink-0 rounded-full border border-gray-300 px-3 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 disabled:opacity-50"
        >
          {serviceability.isPending ? 'Checking…' : 'Check'}
        </button>
      </form>

      {touched && !PINCODE_PATTERN.test(pincode) && (
        <p className="mt-2 text-xs text-red-600">Enter a valid 6-digit pincode.</p>
      )}

      {serviceability.data && (
        <div
          role="status"
          className={`mt-3 rounded-xl border px-3 py-2 text-sm ${
            serviceability.data.serviceable
              ? 'border-green-200 bg-green-50 text-green-800'
              : 'border-red-200 bg-red-50 text-red-700'
          }`}
        >
          {serviceability.data.serviceable ? (
            <div className="space-y-1">
              <p className="font-semibold">
                Delivery available
                {serviceability.data.deliveryZoneName ? ` — ${serviceability.data.deliveryZoneName}` : ''}
                {serviceability.data.localityName ? ` (${serviceability.data.localityName})` : ''}
              </p>
              {(serviceability.data.etaMinHours !== null || serviceability.data.etaMaxHours !== null) && (
                <p>
                  Estimated delivery: {serviceability.data.etaMinHours}–{serviceability.data.etaMaxHours} hours
                </p>
              )}
              {serviceability.data.feePaise !== null && (
                <p>
                  Delivery charge:{' '}
                  {serviceability.data.feePaise === 0 ? 'Free' : formatPaise(serviceability.data.feePaise)}
                </p>
              )}
              {serviceability.data.freeDeliveryThresholdPaise !== null && (
                <p>Free delivery above {formatPaise(serviceability.data.freeDeliveryThresholdPaise)}</p>
              )}
              {serviceability.data.minOrderValuePaise !== null && serviceability.data.minOrderValuePaise > 0 && (
                <p>Minimum order: {formatPaise(serviceability.data.minOrderValuePaise)}</p>
              )}
              <p>{serviceability.data.codAvailable ? 'Cash on delivery available' : 'Cash on delivery not available in this area'}</p>
              {serviceability.data.sameDayDeliveryAvailable && <p>Same-day delivery available</p>}
            </div>
          ) : (
            <p>
              {serviceability.data.reason
                ? REASON_COPY[serviceability.data.reason]
                : 'Sorry, we currently deliver only within selected areas of Hyderabad.'}
            </p>
          )}
        </div>
      )}

      {serviceability.isError && (
        <p className="mt-2 text-xs text-red-600">Could not check delivery for this pincode. Please try again.</p>
      )}
    </div>
  );
}
